import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useAutoAlert } from "../hooks/useAutoAlert";
import Button from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import Dropdown from "../components/ui/Dropdown";
import {
  FiPlus,
  FiTrash2,
  FiDollarSign,
  FiKey,
  FiCheck,
  FiSettings,
} from "react-icons/fi";
import { formatSimpleDate } from "../utils/dateUtils";

const LeagueManagement = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // League data
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("");
  const [selectedLeagueDetails, setSelectedLeagueDetails] = useState(null);
  const [leagueMembers, setLeagueMembers] = useState([]);

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Invite user state
  const [inviteData, setInviteData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [inviteStep, setInviteStep] = useState("email"); // 'email' or 'details'
  const [existingUser, setExistingUser] = useState(null);

  // Auto-dismiss alerts after 5 seconds
  useAutoAlert(error, setError);
  useAutoAlert(success, setSuccess);

  const fetchCommissionerLeagues = useCallback(async () => {
    try {
      setLoading(true);

      // Admins should see all leagues, commissioners see only their leagues
      const endpoint = user?.isAdmin
        ? `${API_URL}/leagues`
        : `${API_URL}/leagues/commissioner`;
      const response = await axios.get(endpoint);

      // Handle different response structures
      const leaguesData = user?.isAdmin ? response.data.leagues : response.data;
      setLeagues(leaguesData);

      if (leaguesData.length > 0) {
        setSelectedLeague(leaguesData[0].id);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to fetch leagues";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.isAdmin]);

  const fetchLeagueMembers = useCallback(async () => {
    if (!selectedLeague) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/leagues/${selectedLeague}/members/details`,
      );
      setLeagueMembers(response.data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to fetch league members";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedLeague]);

  useEffect(() => {
    fetchCommissionerLeagues();
  }, [fetchCommissionerLeagues]);

  useEffect(() => {
    if (selectedLeague) {
      // Update selected league details
      const leagueDetails = leagues.find((l) => l.id === selectedLeague);
      setSelectedLeagueDetails(leagueDetails);
      fetchLeagueMembers();
    }
  }, [selectedLeague, leagues, fetchLeagueMembers, setSelectedLeagueDetails]);

  // Auto-select league from URL parameter
  useEffect(() => {
    const leagueParam = searchParams.get("league");
    if (leagueParam && leagues.length > 0) {
      const league = leagues.find((l) => l.id === leagueParam);
      if (league) {
        setSelectedLeague(leagueParam);
      }
    } else if (leagues.length > 0 && !selectedLeague) {
      // Auto-select first league if none selected
      setSelectedLeague(leagues[0].id);
    }
  }, [leagues, searchParams]);

  const handleCheckEmail = async () => {
    if (!inviteData.email.trim()) {
      setError("Email is required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${API_URL}/users/check-email`,
        {
          email: inviteData.email,
        },
        { headers },
      );

      if (response.data.exists) {
        setExistingUser(response.data.user);
        setInviteStep("existing");
      } else {
        setInviteStep("details");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to check email";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!selectedLeague) {
      setError("Please select a league first");
      return;
    }

    if (
      !inviteData.firstName.trim() ||
      !inviteData.lastName.trim() ||
      !inviteData.email.trim()
    ) {
      setError("First name, last name, and email are required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await axios.post(`${API_URL}/leagues/${selectedLeague}/invite`, {
        firstName: inviteData.firstName,
        lastName: inviteData.lastName,
        email: inviteData.email,
      });

      setSuccess("User invited successfully!");
      handleCloseInviteModal();
      fetchLeagueMembers(); // Refresh the members list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to invite user";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExistingUserToLeague = async () => {
    if (!selectedLeague || !existingUser) {
      setError("Missing league or user information");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(
        `${API_URL}/users/${existingUser.id}/add-to-league`,
        {
          leagueId: selectedLeague,
        },
        { headers },
      );

      setSuccess(`${existingUser.name} added to league successfully!`);
      handleCloseInviteModal();
      fetchLeagueMembers(); // Refresh the members list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to add user to league";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteData({ firstName: "", lastName: "", email: "" });
    setInviteStep("email");
    setExistingUser(null);
    setError("");
  };

  const handleApproveUser = async (userId) => {
    if (!selectedLeague) {
      setError("Please select a league first");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await axios.post(
        `${API_URL}/leagues/${selectedLeague}/members/${userId}/approve`,
      );
      setSuccess("User approved successfully!");
      fetchLeagueMembers(); // Refresh the members list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to approve user";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await axios.post(`${API_URL}/users/${userId}/reset-password`);
      setSuccess("Password reset email sent successfully!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to reset password";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (
      !confirm("Are you sure you want to remove this user from the league?")
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await axios.delete(
        `${API_URL}/leagues/${selectedLeague}/members/${userId}`,
      );
      setSuccess("User removed from league successfully!");
      fetchLeagueMembers(); // Refresh the members list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to remove user";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (userId, isPaid) => {
    // Prevent payment changes in free leagues
    if (selectedLeagueDetails && !selectedLeagueDetails.isPaidLeague) {
      setError("Cannot modify payment status in a free league");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await axios.patch(
        `${API_URL}/leagues/${selectedLeague}/members/${userId}`,
        {
          isPaid: !isPaid,
        },
      );

      setSuccess(`User marked as ${!isPaid ? "paid" : "unpaid"} successfully!`);
      fetchLeagueMembers(); // Refresh the members list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to update payment status";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if current league is paid
  const isPaidLeague = selectedLeagueDetails?.isPaidLeague || false;

  // Check if any users are marked as paid (prevents conversion to free league)
  const hasAnyPaidUsers = leagueMembers.some((member) => member.isPaid);

  const handleConvertLeagueType = async () => {
    if (!selectedLeagueDetails) return;

    const newType = !isPaidLeague;
    const action = newType ? "paid" : "free";

    if (
      !window.confirm(
        `Convert this league to a ${action} league? ${!newType ? "All members will be marked as unpaid." : ""}`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      await axios.patch(`${API_URL}/leagues/${selectedLeague}/type`, {
        isPaidLeague: newType,
      });

      setSuccess(`League converted to ${action} league successfully!`);

      // Refresh league details and members
      fetchCommissionerLeagues();
      fetchLeagueMembers();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to convert league type";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user?.isCommissioner && !user?.isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You need commissioner or admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? "h-full flex flex-col" : ""}`}>
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-center`}
        >
          League Management
        </h1>
        <p className="text-gray-600 text-center mt-1">
          Manage your league members and settings
        </p>
      </div>

      {/* League Selection */}
      {leagues.length > 0 && (
        <div className="mb-6">
          <Dropdown
            label="Select League"
            value={selectedLeague}
            onChange={(newLeagueId) => {
              setSelectedLeague(newLeagueId);

              // Update URL parameters
              const newParams = new URLSearchParams(searchParams);
              newParams.set("league", newLeagueId);
              setSearchParams(newParams);
            }}
            options={leagues.map((league) => ({
              value: league.id,
              label: `${league.name} (${league.memberCount || 0} members)`,
            }))}
            placeholder="Choose a league to manage..."
          />
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Members Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">
              League Members
              {selectedLeagueDetails && (
                <span
                  className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    isPaidLeague
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {isPaidLeague ? "Paid League" : "Free League"}
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage member access and settings
            </p>
          </div>
          <div className="flex gap-2">
            {selectedLeagueDetails &&
              // Only show convert button if converting to paid OR if no users are paid (can convert to free)
              (!isPaidLeague || !hasAnyPaidUsers) && (
                <Button
                  variant="outline"
                  onClick={handleConvertLeagueType}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <FiSettings size={16} />
                  Convert to {isPaidLeague ? "Free" : "Paid"} League
                </Button>
              )}
            <Button
              onClick={() => setShowInviteModal(true)}
              disabled={!selectedLeague}
              className="flex items-center gap-2"
            >
              <FiPlus size={16} />
              Invite User
            </Button>
          </div>
        </div>

        {leagueMembers.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No members in this league yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {isPaidLeague && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leagueMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {member.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {member.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.isApproved
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {member.isApproved ? "Approved" : "Pending"}
                      </span>
                    </td>
                    {isPaidLeague && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            member.isPaid
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {member.isPaid ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatSimpleDate(member.joinedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {!member.isApproved && (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleApproveUser(member.id)}
                            disabled={loading}
                          >
                            <FiCheck size={14} className="mr-1" />
                            Approve
                          </Button>
                        )}
                        {isPaidLeague && (
                          <Button
                            size="sm"
                            variant={member.isPaid ? "outline" : "primary"}
                            onClick={() =>
                              handleMarkAsPaid(member.id, member.isPaid)
                            }
                            disabled={loading}
                          >
                            <FiDollarSign size={14} className="mr-1" />
                            {member.isPaid ? "Mark Unpaid" : "Mark Paid"}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResetPassword(member.id)}
                          disabled={loading}
                        >
                          <FiKey size={14} className="mr-1" />
                          Reset Password
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleRemoveUser(member.id)}
                          disabled={loading}
                        >
                          <FiTrash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={handleCloseInviteModal}
        title={
          inviteStep === "email"
            ? "Check User Email"
            : inviteStep === "existing"
              ? "User Already Exists"
              : "Invite New User"
        }
        size="md"
      >
        <div className="space-y-4">
          {inviteStep === "email" && (
            <>
              <Input
                label="Email Address"
                type="email"
                value={inviteData.email}
                onChange={(e) =>
                  setInviteData((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="Enter user's email address..."
                required
              />
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCloseInviteModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCheckEmail}
                  disabled={loading || !inviteData.email.trim()}
                  className="flex-1"
                >
                  {loading ? "Checking..." : "Check Email"}
                </Button>
              </div>
            </>
          )}

          {inviteStep === "existing" && existingUser && (
            <>
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900">
                  User Already Exists
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>{existingUser.name}</strong> ({existingUser.email}) is
                  already registered.
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  Would you like to add them to this league?
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setInviteStep("email")}
                  className="flex-1"
                >
                  Try Different Email
                </Button>
                <Button
                  onClick={handleAddExistingUserToLeague}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? "Adding..." : "Add to League"}
                </Button>
              </div>
            </>
          )}

          {inviteStep === "details" && (
            <>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-900">New User</h4>
                <p className="text-sm text-green-700 mt-1">
                  {inviteData.email} is not registered. Please provide their
                  name to send an invitation.
                </p>
              </div>
              <Input
                label="First Name"
                value={inviteData.firstName}
                onChange={(e) =>
                  setInviteData((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                placeholder="Enter user's first name..."
                required
              />
              <Input
                label="Last Name"
                value={inviteData.lastName}
                onChange={(e) =>
                  setInviteData((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
                placeholder="Enter user's last name..."
                required
              />
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setInviteStep("email")}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleInviteUser}
                  disabled={
                    loading ||
                    !inviteData.firstName.trim() ||
                    !inviteData.lastName.trim()
                  }
                  className="flex-1"
                >
                  {loading ? "Sending..." : "Send Invitation"}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LeagueManagement;
